import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Centralized axios instance for SmartRoster.
 * All requests carry cookies (httpOnly JWT).
 * 401 → attempt /auth/refresh once; if that fails, redirect to /login.
 */
const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  timeout: 15000,
});

let refreshInFlight = null;

const LAST_REF_KEY = 'tw1.last_error_ref';

/** Read + clear the last captured server correlation ref (from a 500). */
export function takeLastErrorRef() {
  try {
    const ref = localStorage.getItem(LAST_REF_KEY) || '';
    if (ref) localStorage.removeItem(LAST_REF_KEY);
    return ref;
  } catch (_) {
    return '';
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    // Capture the server correlation ref from a 500 ("... (ref: xxxx)") so the
    // next "Probleem melden" submission can attach it automatically.
    if (status === 500) {
      const detail = error.response?.data?.detail || '';
      const m = /ref:\s*([0-9a-f]+)/i.exec(detail);
      if (m) {
        try { localStorage.setItem(LAST_REF_KEY, m[1]); } catch (_) { /* ignore */ }
      }
    }

    // Try refresh exactly once per chain
    if (status === 401 && !original._retry && !original.url?.includes('/auth/')) {
      original._retry = true;
      try {
        refreshInFlight = refreshInFlight || api.post('/auth/refresh');
        await refreshInFlight;
        refreshInFlight = null;
        return api(original);
      } catch (refreshErr) {
        refreshInFlight = null;
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/portaal')) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
export { BACKEND_URL };
